import { DateTime } from "luxon"
import { preferredDateFormatCandidates } from "../unified/forwardLogic"

// 여러가지 시간 관련 데이터를 자동으로 파싱함
export function asLuxon(timeData, options = {}) {
  const zone = options.zone || "Asia/Seoul"

  if (timeData == null) {
    return DateTime.now().setZone(zone)
  }

  if (Array.isArray(timeData)) {
    // array가 들어올 때 첫 번째 값이 number/string이고 두 번째 값이 string이면 fromFormat 시도
    if (timeData.length >= 2 &&
      (typeof timeData[0] === "string" || typeof timeData[0] === "number") &&
      typeof timeData[1] === "string") {
      try {
        // number인 경우 string으로 변환
        const dateString = String(timeData[0])
        const dt = DateTime.fromFormat(dateString, timeData[1], options)
        if (dt.isValid) return dt.setZone(zone)
      } catch (e) {
        // fall through to next parsing method
      }
    }
  }

  if (timeData instanceof DateTime) {
    return timeData.setZone(zone)
  }

  if (timeData instanceof Date) {
    return DateTime.fromJSDate(timeData).setZone(zone)
  }

  if (typeof timeData === "number") {
    // determine seconds vs milliseconds by magnitude (10 digits -> seconds)
    const abs = Math.abs(timeData)
    if (abs <= 9999999999) {
      return DateTime.fromSeconds(timeData).setZone(zone)
    }
    return DateTime.fromMillis(timeData).setZone(zone)
  }

  if (typeof timeData === "string") {
    const s = timeData.trim()
    // pure numeric string -> treat as epoch
    if (/^\d+$/.test(s)) {
      if (s.length === 10) return DateTime.fromSeconds(parseInt(s, 10)).setZone(zone)
      if (s.length === 13) return DateTime.fromMillis(parseInt(s, 10)).setZone(zone)
    }

    // Try common Luxon parsers in order of likelihood
    let dt = DateTime.fromISO(s, options)
    if (dt.isValid) return dt.setZone(zone)

    dt = DateTime.fromRFC2822(s, options)
    if (dt.isValid) return dt.setZone(zone)

    dt = DateTime.fromHTTP(s, options)
    if (dt.isValid) return dt.setZone(zone)

    dt = DateTime.fromSQL(s, options)
    if (dt.isValid) return dt.setZone(zone)

    for (const fmt of preferredDateFormatCandidates) {
      dt = DateTime.fromFormat(s, fmt, options)
      if (dt.isValid) return dt.setZone(zone)
    }

    // Fallback to native Date parsing
    const native = new Date(s)
    if (!isNaN(native.getTime())) {
      return DateTime.fromJSDate(native).setZone(zone)
    }

    throw new Error("Unsupported/invalid time string: " + timeData)
  }

  if (typeof timeData === "object") {
    // try constructing from an object (year, month, day, hour, ...)
    try {
      const dt = DateTime.fromObject(timeData, options)
      if (dt.isValid) return dt.setZone(zone)
    } catch (e) {
      // fall through
    }
  }

  throw new Error("Unsupported time data type")
}

export function kst(...args) {
  // date 인자를 넣으면 그 날짜를, 없으면 현재 시간을 KST로!
  if (args.length === 0) {
    return asLuxon(null, { zone: "Asia/Seoul" })
  }

  // 단일 인자가 들어온 경우 asLuxon으로 처리
  if (args.length === 1) {
    return asLuxon(args[0], { zone: "Asia/Seoul" })
  }

  // 여러 인자가 들어온 경우 array로 만들어서 asLuxon으로 처리
  // 이 경우 첫 번째와 두 번째가 문자열이면 fromFormat으로 처리됨
  return asLuxon(args, { zone: "Asia/Seoul" })
}

window.kst = kst

export function distanceOfTimeInWordsWithDirection(toTime, fromTime = new Date(), options = {}) {
  const zone = options.zone || "Asia/Seoul"
  const to = asLuxon(toTime, { zone })
  const from = asLuxon(fromTime, { zone })

  const diff = to.diff(from, ["years", "months", "days", "hours", "minutes", "seconds"]).shiftTo(
    "years",
    "months",
    "days",
    "hours",
    "minutes",
    "seconds"
  )

  const o = diff.toObject()
  const abs = {
    years: Math.abs(o.years || 0),
    months: Math.abs(o.months || 0),
    days: Math.abs(o.days || 0),
    hours: Math.abs(o.hours || 0),
    minutes: Math.abs(o.minutes || 0),
    seconds: Math.abs(Math.floor(o.seconds || 0))
  }

  // Rails' distance_of_time_in_words typically returns the largest single unit
  // (eg. "3분 전"). To behave similarly, pick the first non-zero unit
  // in descending order and only show that one.
  const units = [
    ["years", "년"],
    ["months", "개월"],
    ["days", "일"],
    ["hours", "시간"],
    ["minutes", "분"],
    ["seconds", "초"]
  ]

  let part = null
  for (const [key, label] of units) {
    const val = Math.trunc(abs[key] || 0)
    if (val > 0) {
      part = `${val}${label}`
      break
    }
  }

  // Use strict greater-than to match Ruby's `if to_time > from_time` behavior
  const direction = to.toMillis() > from.toMillis() ? "후" : "전"
  if (!part) return `0초 ${direction}`
  return `${part} ${direction}`
}

export function distanceOfDateInWordsWithDirection(toTime, fromTime = new Date(), options = {}) {
  const zone = options.zone || "Asia/Seoul"
  const important = options.important || "day"
  const humanReadable = options.humanReadable || false

  const to = asLuxon(toTime, { zone })
  const from = asLuxon(fromTime, { zone })

  // 날짜만 비교 (시간은 무시)
  const toDate = to.startOf("day")
  const fromDate = from.startOf("day")

  const days = Math.floor(toDate.diff(fromDate, "days").days)
  const months = (to.year * 12 + to.month) - (from.year * 12 + from.month)
  const years = to.year - from.year
  const absMonths = Math.abs(months)
  const absYears = Math.abs(years)

  if (important === "day") {
    if (absMonths >= 12) {
      if (humanReadable && absYears === 1) {
        return years > 0 ? "내년" : "작년"
      } else {
        return distanceOfTimeInWordsWithDirection(toTime, fromTime, options)
      }
    } else if (Math.abs(days) < 30) {
      switch (days) {
        case 0:
          return "오늘"
        case -2:
          return humanReadable ? "엇그제" : "2일 전"
        case -1:
          return humanReadable ? "어제" : "1일 전"
        case 1:
          return humanReadable ? "내일" : "1일 후"
        case 2:
          return humanReadable ? "내일 모래" : "2일 후"
        default:
          if (days < 0) {
            return `${Math.abs(days)}일 전`
          } else {
            return `${days}일 후`
          }
      }
    } else {
      if (humanReadable && absMonths === 1) {
        return months > 0 ? "다음달" : "저번달"
      } else {
        return months > 0 ? `${absMonths}달 후` : `${absMonths}달 전`
      }
    }
  } else if (important === "month") {
    if (absMonths >= 12) {
      if (humanReadable && absYears === 1) {
        return years > 0 ? "내년" : "작년"
      } else {
        return distanceOfTimeInWordsWithDirection(toTime, fromTime, options)
      }
    } else if (absMonths >= 1) {
      if (humanReadable && absMonths === 1) {
        return months > 0 ? "다음달" : "저번달"
      } else {
        return months > 0 ? `${absMonths}달 후` : `${absMonths}달 전`
      }
    } else {
      switch (days) {
        case 0:
          return "오늘"
        case -2:
          return humanReadable ? "엇그제" : "2일 전"
        case -1:
          return humanReadable ? "어제" : "1일 전"
        case 1:
          return humanReadable ? "내일" : "1일 후"
        case 2:
          return humanReadable ? "내일 모래" : "2일 후"
        default:
          if (days < 0) {
            return `${Math.abs(days)}일 전`
          } else {
            return `${days}일 후`
          }
      }
    }
  } else {
    return `Error: unknown distance importance: ${important}`
  }
}
